/**
 * SmithCronHealthCard (Iter167) — Master Console card that surfaces the
 * latest APScheduler run for the two Smith Protective automations:
 *   • smith_trigger_watchdog  (daily 09:30 UTC — Indeed + Google News)
 *   • smith_all_metros_scrape (daily 04:00 UTC — OSM warehouses FL/TX/GA)
 *
 * Each row shows status (ok / stale / failed / never), last run age,
 * duration, and a one-click "Run Now" button that fires the manual
 * endpoint. Auto-refreshes every 60s.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Activity, RefreshCw, Play, CheckCircle2, AlertTriangle, XCircle, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  ok:     { color: "#10B981", icon: CheckCircle2, label: "OK" },
  stale:  { color: "#F59E0B", icon: AlertTriangle, label: "Atrasado" },
  failed: { color: "#DC2626", icon: XCircle,       label: "Falhou" },
  never:  { color: "#64748B", icon: Clock,         label: "Nunca rodou" },
};

function fmtRelative(iso) {
  if (!iso) return "—";
  try {
    const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return `${Math.floor(diff)}s atrás`;
    if (diff < 3600) return `${Math.floor(diff / 60)}min atrás`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h atrás`;
    return `${Math.floor(diff / 86400)}d atrás`;
  } catch { return "—"; }
}

function fmtDuration(sec) {
  if (sec === null || sec === undefined) return "—";
  if (sec < 60) return `${sec.toFixed(1)}s`;
  if (sec < 3600) return `${(sec / 60).toFixed(1)}min`;
  return `${(sec / 3600).toFixed(1)}h`;
}

export default function SmithCronHealthCard() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkedAt, setCheckedAt] = useState(null);
  const [running, setRunning] = useState({});   // {kind: bool}
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/smith/cron/health`);
      setJobs(r.data?.jobs || []);
      setCheckedAt(r.data?.checked_at || null);
    } catch {
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  const runNow = async (kind) => {
    setRunning((r) => ({ ...r, [kind]: true }));
    setToast(null);
    try {
      await axios.post(`${API}/smith/cron/run/${kind}`);
      setToast({ kind: "success", text: "Execução enfileirada. Atualizando em ~30s…" });
      setTimeout(load, 30_000);
    } catch (e) {
      setToast({ kind: "error", text: e?.response?.data?.detail || "Falha ao disparar cron." });
    } finally {
      setRunning((r) => ({ ...r, [kind]: false }));
    }
  };

  return (
    <div
      className="rounded-xl p-5 border"
      style={{ borderColor: "rgba(201,162,78,0.2)", background: "rgba(255,255,255,0.03)" }}
      data-testid="smith-cron-health-card"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#C9A24E]" />
          <span className="text-[11px] uppercase tracking-wider text-[#C9A24E] font-bold">
            Smith Protective · Cron Health
          </span>
        </div>
        <button
          onClick={load}
          data-testid="smith-cron-refresh"
          className="text-[10.5px] text-white/55 hover:text-white inline-flex items-center gap-1"
          aria-label="Atualizar"
        >
          <RefreshCw className="w-3 h-3" />
          {checkedAt ? fmtRelative(checkedAt) : "Atualizar"}
        </button>
      </div>

      {loading && (
        <div className="text-white/45 text-[12.5px] italic" data-testid="smith-cron-loading">
          Carregando status…
        </div>
      )}

      {!loading && jobs.length === 0 && (
        <div className="text-white/45 text-[12.5px] italic" data-testid="smith-cron-empty">
          Nenhum job de cron encontrado.
        </div>
      )}

      <ul className="space-y-2.5" data-testid="smith-cron-list">
        {jobs.map((job) => {
          const meta = STATUS_META[job.status] || STATUS_META.never;
          const Icon = meta.icon;
          const lr = job.last_run || {};
          const isRunning = !!running[job.kind];
          return (
            <li
              key={job.kind}
              data-testid={`smith-cron-row-${job.kind}`}
              className="rounded-lg border p-3 flex flex-col gap-2"
              style={{ borderColor: `${meta.color}33`, background: `${meta.color}0C` }}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4 shrink-0" style={{ color: meta.color }} />
                    <span className="text-[13px] font-bold text-white truncate">{job.label}</span>
                    <span
                      className="text-[9.5px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                      style={{ background: `${meta.color}22`, color: meta.color }}
                    >
                      {meta.label}
                    </span>
                  </div>
                  <div className="text-[11px] text-white/55 mt-0.5 truncate">
                    {job.description} · {job.schedule}
                  </div>
                </div>
                <button
                  onClick={() => runNow(job.kind)}
                  disabled={isRunning}
                  data-testid={`smith-cron-run-${job.kind}`}
                  className="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-wider border transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                  style={{ borderColor: "#C9A24E55", background: "#C9A24E12", color: "#C9A24E" }}
                >
                  <Play className="w-3 h-3" />
                  {isRunning ? "Enfileirando…" : "Run Now"}
                </button>
              </div>

              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] pl-6">
                <div className="text-white/50">
                  Último run:{" "}
                  <span className="text-white/85 font-mono">
                    {fmtRelative(lr.finished_at || lr.started_at)}
                  </span>
                </div>
                <div className="text-white/50">
                  Duração: <span className="text-white/85 font-mono">{fmtDuration(lr.duration_s)}</span>
                </div>
                {job.kind === "smith_trigger_watchdog" && (
                  <>
                    <div className="text-white/50">
                      Prospects: <span className="text-white/85 font-mono">{lr.scanned ?? "—"}</span>
                    </div>
                    <div className="text-white/50">
                      Triggers +: <span className="text-white/85 font-mono">{lr.added ?? "—"}</span>
                    </div>
                  </>
                )}
                {job.kind === "smith_all_metros_scrape" && (
                  <>
                    <div className="text-white/50">
                      Metros: <span className="text-white/85 font-mono">{lr.metros ?? "—"}</span>
                    </div>
                    <div className="text-white/50">
                      Novos: <span className="text-white/85 font-mono">{lr.inserted ?? "—"}</span>
                    </div>
                  </>
                )}
                {lr.triggered_by && (
                  <div className="text-white/50 col-span-2 truncate">
                    Fonte: <span className="text-white/85 font-mono">{lr.triggered_by}</span>
                  </div>
                )}
                {lr.error && (
                  <div className="col-span-2 text-[10.5px] text-red-400 truncate" title={lr.error}>
                    Erro: {lr.error}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {toast && (
        <div
          data-testid="smith-cron-toast"
          className="mt-3 text-[11px] rounded-md px-3 py-2 border"
          style={{
            borderColor: toast.kind === "success" ? "#10B98155" : "#DC262655",
            background: toast.kind === "success" ? "#10B98112" : "#DC262612",
            color: toast.kind === "success" ? "#10B981" : "#F87171",
          }}
        >
          {toast.text}
        </div>
      )}
    </div>
  );
}
